import { waitForDOMContentLoaded } from "./async-utils";
import configs from "./configs";
import { store } from "./store-instance";
import { chooseEffectiveAppearance } from "./appearance-preference";
import {
  getResolvedAppearance,
  getToolbarOverlayBackground,
  normalizeResolvedThemeResponse,
  PRODUCT_RESOLVED_THEMES
} from "./theme-contract";

const PROPERTY_MAP = {
  "primary-color": "brandPrimary",
  "primary-color-hover": "brandHover",
  "primary-color-pressed": "brandPressed",
  "action-color": "backgroundSurface2",
  "action-color-highlight": "backgroundSurface3",
  "action-text-color": "textOnBrand",
  "action-label-color": "brandPrimary",
  "action-color-disabled": "textDisabled",
  "action-subtitle-color": "textSecondary",
  "notice-background-color": "backgroundSurface1",
  "notice-text-color": "textPrimary",
  "navbar-background": "backgroundCanvas",
  "navbar-text-color": "textPrimary",
  "text5-color": "textInverse",
  "text4-color": "textPrimary",
  "loading-screen-background": "backgroundCanvas",
  "text-primary": "textPrimary",
  "text-on-brand": "textOnBrand",
  "toolbar-icon-selected-bg": "backgroundSurface3",
  "toolbar-icon-color": "textPrimary",
  "accent1-color": "backgroundSurface1",
  "accent1-color-hover": "backgroundSurface2",
  "accent1-color-pressed": "textPrimary",
  "accent1-border-color": "borderDefault",
  "accent2-color": "backgroundSurface1",
  "accent2-color-hover": "backgroundSurface2",
  "accent2-color-pressed": "textPrimary",
  "accent2-border-color": "borderDefault",
  "accent3-color": "backgroundSurface1",
  "accent3-color-hover": "backgroundSurface2",
  "accent3-color-pressed": "textPrimary",
  "accent3-border-color": "borderDefault",
  "accent4-color": "brandPrimary",
  "accent4-color-hover": "brandHover",
  "accent4-color-pressed": "brandPressed",
  "accent4-border-color": "brandPrimary",
  "accent5-color": "backgroundSurface1",
  "accent5-color-hover": "backgroundSurface2",
  "accent5-color-pressed": "textPrimary",
  "accent5-border-color": "borderDefault",
  "link-color": "brandPrimary",
  "link-color-hover": "brandHover",
  "link-color-pressed": "brandPressed",
  "accept-color-pressed": "brandPressed",
  "mic-setup-modal-bg-color": "backgroundSurface1",
  "nametag-color": "backgroundSurface1",
  "nametag-text-color": "textPrimary",
  "nametag-volume-color": "success",
  "nametag-border-color": "success",
  "nametag-border-color-raised-hand": "warning",
  "favorited-color": "warning"
};

const VIEWER_BACKGROUNDS = {
  dark: "linear-gradient(rgb(55, 59, 71) 0%, rgb(90, 104, 123) 100%)",
  light: "linear-gradient(#E7EDF5 0%, #C7D3E1 100%)"
};

const PARTICIPANT_STAGE_BACKGROUNDS = {
  dark: "#46505C",
  light: "#E7EDF5"
};

const INSPECT_BACKDROP_COLORS = {
  dark: "#050508",
  light: "#E7EDF5"
};

export function getInspectBackdropColor(appearance) {
  return INSPECT_BACKDROP_COLORS[appearance] || INSPECT_BACKDROP_COLORS.dark;
}

function systemAppearance() {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function getThemeResponse() {
  return normalizeResolvedThemeResponse(
    configs.APP_CONFIG?.theme || (typeof window !== "undefined" ? window.APP_CONFIG?.theme : null)
  );
}

export function getCurrentTheme() {
  const response = getThemeResponse();
  const appearance = chooseEffectiveAppearance({
    preference: store.state?.preferences?.appearance,
    systemAppearance: systemAppearance(),
    allowed: response.effectiveConfig.appearance.allowed,
    defaultAppearance: response.effectiveConfig.appearance.default
  });
  return getResolvedAppearance(response, appearance);
}

export function createRuntimeTheme(resolvedTheme) {
  const color = resolvedTheme?.color;
  if (!color || Object.values(PROPERTY_MAP).some(role => !color[role])) return null;
  const properties = Object.fromEntries(
    Object.entries(PROPERTY_MAP).map(([property, role]) => [`--${property}`, color[role]])
  );
  return {
    appearance: resolvedTheme.appearance,
    properties: {
      ...properties,
      "--primaryColorBackground": color.backgroundCanvas,
      "--secondaryColorBackground": color.backgroundSurface2,
      "--toolbar-overlay-background": getToolbarOverlayBackground(resolvedTheme.appearance),
      "--Gray-3": color.textMuted,
      "--Red": color.danger,
      "--color-text-primary": color.textPrimary,
      "--color-text-secondary": color.textSecondary,
      "--color-border-default": color.borderDefault,
      "--color-focus": color.borderFocus,
      "--color-overlay": color.backgroundOverlay,
      "--color-success": color.success,
      "--color-warning": color.warning,
      "--color-danger": color.danger,
      "--color-information": color.information,
      "--color-brand-subtle": color.brandSubtle,
      "--xr-viewer-background": VIEWER_BACKGROUNDS[resolvedTheme.appearance] || VIEWER_BACKGROUNDS.dark,
      "--xr-participant-stage-background":
        PARTICIPANT_STAGE_BACKGROUNDS[resolvedTheme.appearance] || PARTICIPANT_STAGE_BACKGROUNDS.dark
    }
  };
}

export function applyRuntimeTheme(runtimeTheme, root = document.documentElement) {
  if (!runtimeTheme?.appearance || !runtimeTheme.properties || !root) return false;
  Object.entries(runtimeTheme.properties).forEach(([name, value]) => root.style.setProperty(name, value));
  root.dataset.theme = runtimeTheme.appearance;
  document.body?.classList.toggle("dark-theme", runtimeTheme.appearance === "dark");
  document.body?.classList.toggle("light-theme", runtimeTheme.appearance === "light");
  return true;
}

export function getThemeVariables() {
  return createRuntimeTheme(getCurrentTheme())?.properties || {};
}
export function getThemeColor(name) {
  const role = PROPERTY_MAP[name];
  return role ? getCurrentTheme().color[role] : undefined;
}
export function getDefaultTheme() {
  return PRODUCT_RESOLVED_THEMES.dark;
}
export function tryGetTheme() {
  return getCurrentTheme();
}
export const themes = PRODUCT_RESOLVED_THEMES;

export function registerDarkModeQuery(listener) {
  const query =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: dark)")
      : { matches: false, addEventListener() {}, removeEventListener() {} };
  if (query.addEventListener) query.addEventListener("change", listener);
  else query.addListener(listener);
  return [
    query,
    () => (query.removeEventListener ? query.removeEventListener("change", listener) : query.removeListener(listener))
  ];
}

export function onThemeChanged(listener) {
  let removeMediaListener;
  const update = () => {
    const shouldObserveSystem = store.state?.preferences?.appearance === "system";
    if (shouldObserveSystem && !removeMediaListener) {
      [, removeMediaListener] = registerDarkModeQuery(update);
    } else if (!shouldObserveSystem && removeMediaListener) {
      removeMediaListener();
      removeMediaListener = undefined;
    }
    const runtimeTheme = createRuntimeTheme(getCurrentTheme());
    applyRuntimeTheme(runtimeTheme);
    listener?.(runtimeTheme);
  };
  store.addEventListener("statechanged", update);
  update();
  return () => {
    store.removeEventListener("statechanged", update);
    removeMediaListener?.();
  };
}

export function applyThemeToTextButton(el, highlighted) {
  const color = getCurrentTheme().color;
  el.setAttribute("text-button", "textColor", highlighted ? color.danger : color.textPrimary);
  el.setAttribute("text-button", "textHoverColor", highlighted ? color.danger : color.textPrimary);
}

export function colorToVec3(hex) {
  const value = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  return value
    ? `vec3(${(parseInt(value[1], 16) / 255).toFixed(3)}, ${(parseInt(value[2], 16) / 255).toFixed(3)}, ${(
        parseInt(value[3], 16) / 255
      ).toFixed(3)})`
    : null;
}

function updateTextButtonColors() {
  const color = getCurrentTheme().color;
  [
    ["#rounded-text-button", color.textPrimary, color.textPrimary, color.backgroundSurface2, color.backgroundSurface3],
    ["#rounded-button", color.textPrimary, color.textPrimary, color.backgroundSurface2, color.backgroundSurface3],
    [
      "#rounded-text-action-button",
      color.textPrimary,
      color.textPrimary,
      color.backgroundSurface2,
      color.backgroundSurface3
    ],
    [
      "#rounded-action-button",
      color.textPrimary,
      color.textPrimary,
      color.backgroundSurface2,
      color.backgroundSurface3
    ],
    [
      "#rounded-transparent-action-button",
      color.textPrimary,
      color.textPrimary,
      color.backgroundSurface2,
      color.backgroundSurface3
    ],
    ["#rounded-branded-action-button", color.textOnBrand, color.textOnBrand, color.brandPrimary, color.brandHover],
    ["#rounded-branded-text-action-button", color.textOnBrand, color.textOnBrand, color.brandPrimary, color.brandHover],
    ["#fully-rounded-branded-action-button", color.textOnBrand, color.textOnBrand, color.brandPrimary, color.brandHover]
  ].forEach(([selector, text, textHover, background, backgroundHover]) => {
    document
      .querySelector(selector)
      ?.setAttribute(
        "text-button",
        `textColor: ${text}; textHoverColor: ${textHover}; backgroundColor: ${background}; backgroundHoverColor: ${backgroundHover}`
      );
  });
}

waitForDOMContentLoaded().then(() => {
  if (process.env.NODE) return;
  applyRuntimeTheme(createRuntimeTheme(getCurrentTheme()));
  updateTextButtonColors();
  onThemeChanged(updateTextButtonColors);
});
